import axios from 'axios';
import { ChargingStation, ChargingSession, DashboardStats } from '../types';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080/ccms/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor for debugging
api.interceptors.request.use(
  (config) => {
    console.log(`API Request: ${config.method?.toUpperCase()} ${config.url}`);
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    console.error('API Error:', error.response?.data || error.message);
    return Promise.reject(error);
  }
);

// Charging Station API
export const chargingStationApi = {
  // Get all stations
  getAllStations: async (): Promise<ChargingStation[]> => {
    const response = await api.get('/stations');
    return response.data;
  },

  // Get single station
  getStation: async (stationId: string): Promise<ChargingStation> => {
    const response = await api.get(`/stations/${stationId}`);
    return response.data;
  },

  // Create station
  createStation: async (station: Partial<ChargingStation>): Promise<ChargingStation> => {
    const response = await api.post('/stations', station);
    return response.data;
  },

  // Update station
  updateStation: async (stationId: string, station: Partial<ChargingStation>): Promise<ChargingStation> => {
    const response = await api.put(`/stations/${stationId}`, station);
    return response.data;
  },

  // Delete station
  deleteStation: async (stationId: string): Promise<void> => {
    await api.delete(`/stations/${stationId}`);
  },

  // Get offline stations
  getOfflineStations: async (): Promise<ChargingStation[]> => {
    const response = await api.get('/stations/offline');
    return response.data;
  },

  // Remote start transaction
  remoteStart: async (stationId: string, connectorId: number, idTag: string): Promise<string> => {
    const response = await api.post(`/stations/${stationId}/remote-start`, null, {
      params: { connectorId, idTag }
    });
    return response.data;
  },

  // Remote stop transaction
  remoteStop: async (stationId: string, transactionId: string): Promise<string> => {
    const response = await api.post(`/stations/${stationId}/remote-stop`, null, {
      params: { transactionId }
    });
    return response.data;
  },

  // Unlock connector
  unlockConnector: async (stationId: string, connectorId: number): Promise<string> => {
    const response = await api.post(`/stations/${stationId}/unlock`, null, {
      params: { connectorId }
    });
    return response.data;
  },
};

// Charging Session API
export const chargingSessionApi = {
  // Get all sessions
  getAllSessions: async (): Promise<ChargingSession[]> => {
    const response = await api.get('/sessions');
    return response.data;
  },

  // Get session by ID
  getSession: async (sessionId: string): Promise<ChargingSession> => {
    const response = await api.get(`/sessions/${sessionId}`);
    return response.data;
  },

  // Get sessions by station
  getSessionsByStation: async (stationId: string): Promise<ChargingSession[]> => {
    const response = await api.get(`/stations/${stationId}/sessions`);
    return response.data;
  },
};

// Dashboard API
export const dashboardApi = {
  // Get dashboard statistics
  getStats: async (): Promise<DashboardStats> => {
    const response = await api.get('/dashboard/stats');
    return response.data;
  },
};

export default api;